import { Component, computed, inject, input, output } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { StepsModule } from 'primeng/steps';
import { MenuItem } from 'primeng/api';
import { WizardStep, ActivityTypeEnum } from '../../models/wizard.models';
import { ResizeService } from '@core/services/mobile/resize.service';
import { ActivityWizardStore } from '../../store/activity-wizard.store';

@Component({
  selector: 'app-wizard-navigation',
  templateUrl: './wizard-navigation.component.html',
  styleUrls: ['./wizard-navigation.component.scss'],
  standalone: true,
  imports: [TranslateModule, ButtonModule, StepsModule],
})
export class WizardNavigationComponent {
  readonly steps = input<WizardStep[]>([]);
  readonly currentStepIndex = input<number>(0);
  readonly canGoBack = input<boolean>(false);
  readonly canGoNext = input<boolean>(false);
  readonly isLastStep = input<boolean>(false);

  readonly stepClick = output<number>();
  readonly previous = output<void>();
  readonly next = output<void>();
  readonly save = output<void>();
  readonly cancel = output<void>();
  public readonly resizeService = inject(ResizeService);
  public readonly wizardStore = inject(ActivityWizardStore);

  saveButtonText = computed<string>(() => {
    const wizardData = this.wizardStore.wizardData();
    const activityTypeId = wizardData?.activityType?.id;

    // servizio
    if (activityTypeId === ActivityTypeEnum.RACCOLTA_PORTA_A_PORTA) {
      return 'Salva servizio';
    }

    // Check if it's a CRM ticket activity
    if (
      activityTypeId === ActivityTypeEnum.NUOVO_TICKET_TARI ||
      activityTypeId === ActivityTypeEnum.NUOVO_TICKET_RIFIUTI ||
      activityTypeId === ActivityTypeEnum.NUOVO_TICKET_RITIRO_ON_DEMAND
    ) {
      return 'WIZARD.NAVIGATION.SAVE_TICKET';
    }

    // Default to "Salva Attività"
    return 'WIZARD.NAVIGATION.SAVE_ACTIVITY';
  });

  // Convert steps to PrimeNG MenuItem format for the Steps component
  menuItems = computed<MenuItem[]>(() => {
    return this.steps().map((step, index) => ({
      label: step.title,
      icon: this.getStepIcon(step, index),
      command: () => this.onStepClick(index),
      disabled: !this.canNavigateToStep(index),
      class: this.getStepStyleClass(step, index),
    }));
  });

  // Get the active step index for PrimeNG Steps component
  activeIndex = computed(() => this.currentStepIndex());

  getStepIcon(step: WizardStep, index: number): string {
    // Error state - completed but invalid
    if (step.isCompleted && !step.isValid) {
      return 'pi pi-exclamation-triangle';
    }

    // Completed and valid
    if (step.isCompleted && step.isValid) {
      return 'pi pi-check';
    }

    // Current step
    const currentStepIndex = this.currentStepIndex();
    if (index === currentStepIndex) {
      return 'pi pi-circle-fill';
    }

    // Future steps
    if (index > currentStepIndex) {
      // Accessible future step
      if (this.canNavigateToStep(index)) {
        return 'pi pi-circle';
      }
      // Blocked future step
      return 'pi pi-lock';
    }

    // Past steps (should be completed, but fallback)
    return 'pi pi-circle';
  }

  getStepStyleClass(step: WizardStep, index: number): string {
    const classes: string[] = [];

    // Skipped status (highest priority)
    if (step.isSkipped) {
      classes.push('step-skipped');
      return classes.join(' '); // Return early for skipped steps
    }

    // Completion status &&
    if (step.isCompleted && step.isValid && index > this.currentStepIndex()) {
      classes.push('not-active');
    } else if (step.isCompleted && !step.isValid) {
      classes.push('step-error');
    }
    // Completion status
    if (step.isCompleted && step.isValid) {
      classes.push('step-completed');
    } else if (step.isCompleted && !step.isValid) {
      classes.push('step-error');
    }

    // Current step
    const currentStepIndex = this.currentStepIndex();
    if (index === currentStepIndex) {
      classes.push('step-active');
    }

    // Navigation status
    if (!this.canNavigateToStep(index)) {
      classes.push('step-disabled');
    } else if (index !== currentStepIndex) {
      classes.push('step-clickable');
    }

    // Required vs optional
    if (step.isRequired) {
      classes.push('step-required');
    } else {
      classes.push('step-optional');
    }

    // Progress status
    if (index < currentStepIndex) {
      classes.push('step-past');
    } else if (index > currentStepIndex) {
      classes.push('step-future');
    }

    return classes.join(' ');
  }

  canNavigateToStep(index: number): boolean {
    // Can always navigate to current step
    const currentStepIndex = this.currentStepIndex();
    if (
      index === currentStepIndex &&
      !this.steps()[currentStepIndex].isSkipped
    ) {
      return true;
    }

    // Can always navigate to previous steps
    if (index < currentStepIndex && !this.steps()[currentStepIndex].isSkipped) {
      return true;
    }

    // For forward navigation, check step completion and validity
    if (index > currentStepIndex) {
      // Check if all intermediate steps are completed
      for (let i = currentStepIndex; i < index; i++) {
        const step = this.steps()[i];
        if (!step) return false;

        // Required steps must be completed and valid
        if (step.isRequired && (!step.isCompleted || !step.isValid)) {
          return false;
        }
      }

      // Special case: can navigate to immediate next step if current step is valid
      if (index === currentStepIndex + 1) {
        const currentStep = this.steps()[currentStepIndex];
        return (
          currentStep?.isValid ||
          !currentStep?.isRequired ||
          !currentStep?.isSkipped ||
          false
        );
      }

      return true;
    }

    return false;
  }

  onStepClick(index: number): void {
    if (this.canNavigateToStep(index)) {
      this.stepClick.emit(index);
    }
  }

  onPrevious(): void {
    if (this.canGoBack()) {
      // TODO: The 'emit' function requires a mandatory void argument
      this.previous.emit();
    }
  }

  onNext(): void {
    if (this.canGoNext()) {
      // TODO: The 'emit' function requires a mandatory void argument
      this.next.emit();
    }
  }

  onSave(): void {
    // TODO: The 'emit' function requires a mandatory void argument
    this.save.emit();
  }

  onCancel(): void {
    // TODO: The 'emit' function requires a mandatory void argument
    this.cancel.emit();
  }

  // Get progress percentage for visual indicator
  getProgressPercentage(): number {
    const steps = this.steps();
    if (steps.length === 0) {
      return 0;
    }
    return Math.round(((this.currentStepIndex() + 1) / steps.length) * 100);
  }

  // Get completed steps count
  getCompletedStepsCount(): number {
    return this.steps().filter((step) => step.isCompleted).length;
  }

  // Get validation status summary
  getValidationSummary(): {
    totalSteps: number;
    completedSteps: number;
    validSteps: number;
    requiredSteps: number;
    completedRequiredSteps: number;
    hasErrors: boolean;
  } {
    const totalSteps = this.steps().length;
    const completedSteps = this.steps().filter(
      (step) => step.isCompleted,
    ).length;
    const validSteps = this.steps().filter(
      (step) => step.isCompleted && step.isValid,
    ).length;
    const requiredSteps = this.steps().filter((step) => step.isRequired).length;
    const completedRequiredSteps = this.steps().filter(
      (step) => step.isRequired && step.isCompleted,
    ).length;
    const hasErrors = this.steps().some(
      (step) => step.isCompleted && !step.isValid,
    );

    return {
      totalSteps,
      completedSteps,
      validSteps,
      requiredSteps,
      completedRequiredSteps,
      hasErrors,
    };
  }

  // Check if wizard can be completed
  canCompleteWizard(): boolean {
    const summary = this.getValidationSummary();
    return (
      summary.completedRequiredSteps === summary.requiredSteps &&
      !summary.hasErrors
    );
  }

  // Get next required step that needs completion
  getNextRequiredStep(): WizardStep | null {
    return (
      this.steps()
        .slice(this.currentStepIndex())
        .find((step) => step.isRequired && !step.isCompleted) || null
    );
  }

  // Get step tooltip text for better UX
  getStepTooltip(step: WizardStep, index: number): string {
    if (step.isSkipped) {
      return `${step.title} - Saltato automaticamente`;
    }

    if (step.isCompleted && step.isValid) {
      return `${step.title} - Completato`;
    }

    if (step.isCompleted && !step.isValid) {
      return `${step.title} - Errore: richiede correzioni`;
    }

    if (index === this.currentStepIndex()) {
      return `${step.title} - Step corrente`;
    }

    if (!this.canNavigateToStep(index)) {
      if (step.isRequired) {
        return `${step.title} - Completa i passaggi precedenti`;
      } else {
        return `${step.title} - Opzionale`;
      }
    }

    return step.title;
  }

  disabledNextMobileWithoutFirma(): boolean {
    return (
      this.steps()[this.currentStepIndex()]?.title == 'Consegna' &&
      this.resizeService.isMobile() &&
      !!!this.wizardStore.wizardData()?.firma
    );
  }

  showInfoUser(ev: any) {}
}
